import { useEffect, useState } from "react";
import Label from "../../../../components/form_fields/Label";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import { useForm } from 'react-hook-form';
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { AiOutlineDownCircle } from "react-icons/ai";
import "react-phone-input-2/lib/style.css";
import { PermissionChecker, PERMISSIONS } from "../../../../helpers/usePermissionManager";
import useUserStore from "../../../../state-management/useUserStore";
import { useGetCompanyLevel } from "../../../../services/company-level.service";
import { getAccessToken } from "../../../../services/utils/app.data";
import axios from "axios";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import { CompanyLevelOrderBy } from "../../../../models/company-level.dto";

// Helper function to get auth headers
const getAuthHeaders = () => {
    const token = getAccessToken();
    return {
        headers: {
            Authorization: `Bearer ${token}`
        }
    };
};

const TaxLevelMappings = () => {
    const companyProfile = (useUserStore().currentUser.user.companyProfiles || [])[0];
    const companyId = companyProfile?.companyId;
    const currentCompany = useCompanyStore().currentCompany;

    // Accordion state - track which accordion is expanded
    const [expandedAccordion, setExpandedAccordion] = useState<number | null>(null);
    
    // Form state - track which form is being edited
    const [editingLevel, setEditingLevel] = useState<number | null>(null);
    
    // Tax position selection state (keyed by companyLevel.id)
    const [selectedTaxPositions, setSelectedTaxPositions] = useState<{[key: number]: string}>({});
    
    // Tax positions from API
    const [taxPositions, setTaxPositions] = useState<any[]>([]);
    const [loadingTaxPositions, setLoadingTaxPositions] = useState(true);

    // Fetch company levels
    const { data: allCompanyLevels } = useGetCompanyLevel({
                companyId: currentCompany?.id,
                limit: APP_CONFIG.PAGE_LIMIT,
                orderBy: CompanyLevelOrderBy.LEVEL_NAME_ASC,
            });
    const companyLevels = allCompanyLevels?.data || [];

    const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

    // Fetch tax positions from the API
    useEffect(() => {
        const fetchTaxPositions = async () => {
            setLoadingTaxPositions(true);
            try {
                const API_BASE_URL = process.env.REACT_APP_PAYROLL_API;
                const response = await axios.get(`${API_BASE_URL}/api/v1/tax-positions`, getAuthHeaders());
                const positions = response.data?.data || response.data || [];
                setTaxPositions(positions);
            } catch (error) {
                console.error('Error fetching tax positions:', error);
            } finally {
                setLoadingTaxPositions(false);
            }
        };

        fetchTaxPositions();
    }, []);

    const handleTaxPositionChange = (levelId: number, value: string) => {
        setSelectedTaxPositions(prev => ({
            ...prev,
            [levelId]: value
        }));
    };

    const onSubmit = (level: any) => async (data: any) => {
        try {
            const taxPositionId = data[`tax_${level.id}_taxPosition`];

            console.log('Selected tax position for level:', {
                levelId: level.id,
                levelName: level.levelName,
                taxPositionId: parseInt(taxPositionId)
            });

            // TODO: Add your API call here if needed
            
            setEditingLevel(null);
        } catch (error) {
            console.error('Error saving tax position mapping:', error);
        }
    };

    const renderTaxLevelAccordion = (level: any, index: number) => {
        const levelId = level.id;
        const selectedTaxPositionId = selectedTaxPositions[levelId];
        const selectedTaxPosition = taxPositions.find(tp => tp.id == selectedTaxPositionId);
        
        return (
            <Accordion
                key={levelId}
                expanded={expandedAccordion === levelId}
                onChange={() => setExpandedAccordion(expandedAccordion === levelId ? null : levelId)}
                classes={{
                    root: "border border-gray-300 rounded-lg py-2 mb-2",
                }}
            >
                <AccordionSummary
                    expandIcon={<AiOutlineDownCircle className="text-xl" />}
                    aria-controls={`tax-level${levelId}-content`}
                    id={`tax-level${levelId}-header`}
                    sx={{ px: 3, border: "1px" }}
                >
                    <div className="flex justify-between w-full items-center pr-4">
                        <h2 className="font-inter text-sm font-semibold">
                            {level.levelName}
                        </h2>
                        {selectedTaxPosition && (
                            <span className="text-xs text-gray-500">
                                {selectedTaxPosition.name}
                            </span>
                        )}
                    </div>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3 }}>
                    <form onSubmit={handleSubmit(onSubmit(level))}>
                        <div className="border-b-[1.5px] pb-3 flex justify-between font-semibold text-2xs">
                            <h2 className="font-semibold text-2xs">Details</h2>
                            <PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
                                <button
                                    type="button"
                                    className={`border-none text-primary-blue text-2xs ${
                                        editingLevel === levelId ? "hidden" : ""
                                    }`}
                                    onClick={() => setEditingLevel(levelId)}
                                >
                                    Edit
                                </button>
                            </PermissionChecker>
                        </div>

                        <div className="mt-4">
                            <Label
                                title="Tax Position"
                                for={`taxPosition${levelId}`}
                            />
                            <select
                                className={`w-full bg-secondary-gray rounded-lg h-12 px-5 mt-2 text-sm border-none`}
                                disabled={editingLevel !== levelId}
                                value={selectedTaxPositions[levelId] || ''}
                                {...register(`tax_${levelId}_taxPosition`, { required: true })}
                                onChange={(e) => {
                                    handleTaxPositionChange(levelId, e.target.value);
                                    setValue(`tax_${levelId}_taxPosition`, e.target.value);
                                }}
                            >
                                <option value="">
                                    Select Tax Position
                                </option>
                                {taxPositions.map((position, idx) => (
                                    <option key={idx} value={position.value}>
                                        {position.displayName}
                                    </option>
                                ))}
                            </select>
                            {errors[`tax_${levelId}_taxPosition`] && (
                                <p className="text-red-500 text-xs mt-1">Tax position is required</p>
                            )}
                        </div>

                        {editingLevel === levelId && (
                            <div className="flex w-full justify-end space-x-4 items-center mt-10">
                                <button
                                    type="button"
                                    className="px-12 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
                                    onClick={() => {
                                        setEditingLevel(null);
                                        reset();
                                    }}
                                >
                                    Cancel
                                </button>
                                <OrangeButton
                                    title="Save"
                                    className={"px-12"}
                                    type={"submit"}
                                />
                            </div>
                        )}
                    </form>
                </AccordionDetails>
            </Accordion>
        );
    };

    if (loadingTaxPositions) {
        return <div>Loading...</div>;
    }

    return (
       <div>
            <div className="border-b-[1.5px] pb-3 flex justify-between font-semibold text-sm">
                <h2 className="font-semibold text-sm">Tax Level Mappings</h2>
            </div>
            
            <div className="w-full mt-4">
                {companyLevels.length === 0 ? (
                    <p className="text-gray-500 text-sm">No company levels found. Please create company levels first.</p>
                ) : (
                    companyLevels.map((level, index) => renderTaxLevelAccordion(level, index))
                )}
            </div>
       </div>
    );
};

export default TaxLevelMappings;